import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import IconButton from '@mui/material/IconButton';
import { IconHeart } from '../../../../assets/icons/icon-heart';
import { useBackdropContext } from '../../../../context/backdrop';
import { useInstallationContext } from '../../../../context/installation';
import {
  ActionType,
  AddToFavouritesMutation,
  AddToFavouritesMutationVariables,
  DashboardItemType,
  RemoveFromFavouritesMutation,
  RemoveFromFavouritesMutationVariables,
} from '../../../../data-access/gql-types/graphql';
import { ADD_TO_FAVOURITES, REMOVE_FROM_FAVOURITES } from '../../../../data-access/mutations/groups';
import { useRefetchData } from '../../../../hooks/refresh-data/use-refetch-data';
import { useGroups } from '../../hooks/groups/use-groups';

type FavouriteProps = {
  actionId: string;
  actionType: ActionType;
};

const Favourite: React.FC<FavouriteProps> = ({ actionId, actionType }) => {
  const { favouriteGroup } = useGroups();
  const [isActionInGroup, setIsActionInGroup] = useState<boolean>();
  const { selectedInstallationId } = useInstallationContext();
  const { refetchGroups } = useRefetchData();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();

  const [addToFavourite] = useMutation<AddToFavouritesMutation, AddToFavouritesMutationVariables>(ADD_TO_FAVOURITES, {
    onCompleted: (data) => {
      turnOffBackdrop();
      if (data?.addToFavourites?.id) refetchGroups();
    },
  });

  const [removeFromFavourite] = useMutation<RemoveFromFavouritesMutation, RemoveFromFavouritesMutationVariables>(
    REMOVE_FROM_FAVOURITES,
    {
      onCompleted: (data) => {
        turnOffBackdrop();
        if (data?.removeFromFavourites?.id) refetchGroups();
      },
    },
  );

  useEffect(() => {
    setIsActionInGroup(
      !!favouriteGroup?.userGroupItems?.find((item) => {
        if ('itemId' in item) return item.itemId === actionId;
      }),
    );
  }, [favouriteGroup, setIsActionInGroup]);

  const toggleFavouriteGroup = () => {
    turnOnBackdrop();

    if (isActionInGroup) {
      removeFromFavourite({
        variables: {
          input: {
            id: actionId,
            itemType: actionType === ActionType.Advanced ? DashboardItemType.Action : DashboardItemType.Central,
            installationId: selectedInstallationId,
          },
        },
      });
    } else {
      addToFavourite({
        variables: {
          input: {
            id: actionId,
            itemType: actionType === ActionType.Advanced ? DashboardItemType.Action : DashboardItemType.Central,
            installationId: selectedInstallationId,
          },
        },
      });
    }
  };

  return (
    <IconButton aria-label="favourite" size="large" onClick={toggleFavouriteGroup}>
      <IconHeart isEmpty={!isActionInGroup} />
    </IconButton>
  );
};

export default Favourite;
